import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import Avatar from './Avatar';
import { openProfile } from '../lib/nav';

/* Referrals: recommend someone for a specific opportunity, possibly with a
   reward the company pays. Members see an opportunity and one clear action —
   no mechanics to learn. */

const STATUS_LABEL = {
  pending_consent: 'Waiting for them to confirm', new: 'New', interviewing: 'Interviewing',
  hired: 'Hired', not_a_fit: 'Not a fit', declined: 'Declined', withdrawn: 'Withdrawn',
};
const money = (n, cur = 'USD') => `${cur === 'USD' ? '$' : `${cur} `}${Number(n).toLocaleString()}`;

/* The "Refer someone" dialog: someone on B/N (they confirm first) or outside. */
function ReferDialog({ post, notify, onClose, onSent }) {
  const [onBN, setOnBN] = useState(true);
  const [q, setQ] = useState('');
  const [people, setPeople] = useState([]);
  const [who, setWho] = useState(null);
  const [name, setName] = useState('');
  const [mail, setMail] = useState('');
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!onBN) return undefined;
    const t = setTimeout(() => api.discover(q.trim()).then((rows) => setPeople(rows.map((r) => r.person))).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q, onBN]);
  const f = post.fields || {};
  const ready = note.trim().length >= 10 && consent && (onBN ? Boolean(who) : name.trim() && /.+@.+\..+/.test(mail));
  async function send() {
    setBusy(true);
    try {
      const r = await api.refer(post.id, onBN
        ? { candidate_member_id: who.id, note: note.trim(), consent }
        : { candidate_name: name.trim(), candidate_email: mail.trim(), note: note.trim(), consent });
      notify?.(r.status === 'pending_consent' ? `Sent — ${who.name.split(' ')[0]} confirms first` : 'Referral sent');
      onSent?.();
      onClose();
    } catch (e) { notify?.(e.message); } finally { setBusy(false); }
  }
  return (
    <div className="modal-back" role="presentation" onClick={onClose}>
      <div className="modal card pad" role="dialog" aria-modal="true" aria-labelledby="rf-title" onClick={(e) => e.stopPropagation()}>
        <span className="rf-eyebrow">Refer · {post.title}{f.company ? ` · ${f.company}` : ''}</span>
        <h2 id="rf-title">Who would be great for this?</h2>
        <div className="rf-switch" role="radiogroup" aria-label="Who you're referring">
          <button type="button" role="radio" aria-checked={onBN} className={onBN ? 'on' : ''} onClick={() => setOnBN(true)}>Someone on B/N</button>
          <button type="button" role="radio" aria-checked={!onBN} className={!onBN ? 'on' : ''} onClick={() => setOnBN(false)}>Someone not on B/N yet</button>
        </div>
        {onBN ? (
          who ? (
            <div className="rf-picked">
              <Avatar person={who} className="prop-av sm" /> <span><b>{who.name}</b> <span className="muted-sm">{who.headline}</span></span>
              <button type="button" className="linklike" onClick={() => setWho(null)}>change</button>
            </div>
          ) : (
            <>
              <input className="in" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name or skill…" aria-label="Find the person" autoFocus />
              <div className="rf-results">
                {people.slice(0, 6).map((p) => (
                  <button key={p.id} type="button" className="rf-person" onClick={() => setWho(p)}>
                    <Avatar person={p} className="prop-av sm" /> <span><b>{p.name}</b> <span className="muted-sm">{p.headline}</span></span>
                  </button>
                ))}
              </div>
            </>
          )
        ) : (
          <div className="rf-outside">
            <input className="in" value={name} onChange={(e) => setName(e.target.value)} placeholder="Their name" aria-label="Their name" />
            <input className="in" type="email" value={mail} onChange={(e) => setMail(e.target.value)} placeholder="Their email" aria-label="Their email" />
          </div>
        )}
        <label className="flabel" htmlFor="rf-why">Why they're a fit (the poster sees this)</label>
        <textarea id="rf-why" className="ta" rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Built settlement contracts at two exchanges, strong on audits, open to remote." />
        <label className="rf-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span><b>They know and are happy to be referred.</b><br />
            <span className="muted-sm">{onBN ? 'They also get a request to confirm before the poster sees their name.' : 'Only share someone’s details with their permission.'}</span></span>
        </label>
        {f.reward_amount ? (
          <p className="muted-sm">If they're hired, {f.company || 'the company'} pays the {money(f.reward_amount, f.reward_currency)} reward to you directly ({f.reward_terms}). B/N tracks the referral but doesn't handle money.</p>
        ) : null}
        <div className="modal-actions">
          <button type="button" className="btn quiet sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn apply sm" disabled={!ready || busy} onClick={send}>{busy ? 'Sending…' : 'Send referral'}</button>
        </div>
      </div>
    </div>
  );
}

/* Poster: every referral, with status and the reward steps. Referrer: their own. */
function Tracker({ post, notify }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.postReferrals(post.id).then(setRows).catch(() => setRows([])), [post.id]);
  useEffect(() => { load(); }, [load]);
  const act = (p) => p.then(load).catch((e) => notify?.(e.message));
  if (!rows) return <p className="muted-sm">Loading referrals…</p>;
  if (!rows.length) return <p className="muted-sm">No referrals yet.</p>;
  return (
    <div className="rf-tracker">
      {rows.map((r) => (
        <div key={r.id} className="rf-row">
          <div className="rf-row-top">
            <b>{r.candidate ? (
              <button type="button" className="person-link name" onClick={() => openProfile(r.candidate.handle)}><b>{r.candidate.name}</b></button>
            ) : r.candidate_name}</b>
            {r.candidate_email && <a className="muted-sm" href={`mailto:${r.candidate_email}`}>{r.candidate_email}</a>}
            <span className="muted-sm">{r.role === 'poster' && r.referrer ? `referred by ${r.referrer.name}` : ''}</span>
            <span className={`rf-status s-${r.status}`}>{STATUS_LABEL[r.status]}{r.status === 'hired' && r.reward_amount ? (r.reward_received ? ' · reward received' : r.reward_paid ? ' · reward paid' : ' · reward due') : ''}</span>
          </div>
          {r.note && <p className="rf-note">“{r.note}”</p>}
          {r.role === 'poster' && !['pending_consent', 'declined', 'withdrawn'].includes(r.status) && (
            <div className="rf-actions" role="group" aria-label="Status">
              {['new', 'interviewing', 'hired', 'not_a_fit'].map((st) => (
                <button key={st} type="button" className={`rf-chip${r.status === st ? ' on' : ''}`}
                  onClick={() => r.status !== st && act(api.referralStatus(r.id, st))}>{STATUS_LABEL[st]}</button>
              ))}
              {r.status === 'hired' && r.reward_amount > 0 && !r.reward_paid && (
                <button type="button" className="btn apply sm" onClick={() => act(api.referralPaid(r.id))}>Mark reward paid</button>
              )}
            </div>
          )}
          {r.role === 'referrer' && r.reward_paid && !r.reward_received && (
            <button type="button" className="btn apply sm" onClick={() => act(api.referralReceived(r.id))}>I received the reward</button>
          )}
          {r.role === 'referrer' && ['pending_consent', 'new'].includes(r.status) && (
            <button type="button" className="linklike" onClick={() => act(api.withdrawReferral(r.id))}>Withdraw</button>
          )}
        </div>
      ))}
    </div>
  );
}

/* The opportunity block inside a referral post's card. */
export default function ReferralPanel({ item, mine, notify, onChanged }) {
  const [referring, setReferring] = useState(false);
  const [showing, setShowing] = useState(false);
  const f = item.fields || {};
  const closed = item.state === 'filled';
  return (
    <div className="rf-panel" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
      <div className="rf-meta">
        <span className="rf-company">{f.company ? `Hiring at ${f.company}` : 'Hiring opportunity'}</span>
        <span className={`rf-open${closed ? ' closed' : ''}`}>{closed ? 'Filled' : item.state === 'pending' ? 'Awaiting approval' : 'Open'}</span>
      </div>
      {(f.tags || []).length > 0 && <div className="fc-tags">{f.tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>}
      {f.link && <a className="rf-link" href={f.link} target="_blank" rel="noopener noreferrer">Job details ↗</a>}
      <div className="rf-reward">
        <div>
          {f.reward_amount ? (<>
            <span className="muted-sm">Referral reward</span>
            <b className="rf-amount">{money(f.reward_amount, f.reward_currency)}</b>
            <span className="muted-sm">{f.reward_terms} Paid by {f.company || 'the company'} directly.</span>
          </>) : (
            <span className="muted-sm">Know someone who'd be a great fit? Recommend them.</span>
          )}
        </div>
        {!mine && !closed && item.state === 'open' && (
          <button type="button" className="btn rf-cta" onClick={() => setReferring(true)}>Refer someone →</button>
        )}
      </div>
      <button type="button" className="linklike rf-count" onClick={() => setShowing(!showing)} aria-expanded={showing}>
        {mine ? `Referrals (${item.referral_count || 0}) ${showing ? '▾' : '▸'}` : `${item.referral_count || 0} referral${item.referral_count === 1 ? '' : 's'} so far${showing ? '' : ' · see yours'}`}
      </button>
      {showing && <Tracker post={item} notify={notify} />}
      {referring && <ReferDialog post={item} notify={notify} onClose={() => setReferring(false)} onSent={onChanged} />}
    </div>
  );
}

/* People → Requests: referrals waiting for you to confirm, and ones you made. */
export function MyReferrals({ notify, onChanged }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.myReferrals().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const asks = rows.filter((r) => r.role === 'candidate' && r.status === 'pending_consent');
  const mineAsCandidate = rows.filter((r) => r.role === 'candidate' && r.status !== 'pending_consent');
  const withdraw = (id) => api.withdrawReferral(id).then(load).then(() => notify?.('Referral withdrawn')).catch((e) => notify?.(e.message));
  const made = rows.filter((r) => r.role === 'referrer');
  if (!rows.length) return null;
  return (
    <>
      {asks.length > 0 && <h2 className="sec-title">Referred for an opportunity ({asks.length})</h2>}
      {asks.map((r) => (
        <div key={r.id} className="card pad rf-ask">
          <b>{r.referrer?.name} referred you for {r.post_title}{r.company ? ` at ${r.company}` : ''}</b>
          <span className="muted-sm">“{r.note}”</span>
          <span className="muted-sm">Confirm and the poster sees your name and profile. Decline and nobody is told.</span>
          <div className="modal-actions">
            <button className="btn quiet sm" onClick={() => api.declineReferral(r.id).then(load).then(() => { notify?.('Declined, quietly.'); onChanged?.(); }).catch((e) => notify?.(e.message))}>Decline</button>
            <button className="btn apply sm" onClick={() => api.confirmReferral(r.id).then(load).then(() => { notify?.('Confirmed — the poster can see you now'); onChanged?.(); }).catch((e) => notify?.(e.message))}>Confirm</button>
          </div>
        </div>
      ))}
      {mineAsCandidate.length > 0 && <h2 className="sec-title">Opportunities you were referred for ({mineAsCandidate.length})</h2>}
      {mineAsCandidate.map((r) => (
        <div key={r.id} className="person-row">
          <div className="person-main static">
            <b>{r.post_title}{r.company ? ` at ${r.company}` : ''}</b>
            <span className="muted-sm">Referred by {r.referrer?.name} · {STATUS_LABEL[r.status]}</span>
          </div>
        </div>
      ))}
      {made.length > 0 && <h2 className="sec-title">Your referrals ({made.length})</h2>}
      {made.map((r) => (
        <div key={r.id} className="person-row">
          <div className="person-main static">
            <b>{r.candidate?.name || r.candidate_name} → {r.post_title}</b>
            <span className="muted-sm">{r.company} · {STATUS_LABEL[r.status]}{r.reward_paid && !r.reward_received ? ' · reward marked paid' : ''}</span>
          </div>
          {r.reward_paid && !r.reward_received && (
            <button className="btn apply sm" onClick={() => api.referralReceived(r.id).then(load).catch((e) => notify?.(e.message))}>I received it</button>
          )}
          {['pending_consent', 'new'].includes(r.status) && (
            <button className="btn quiet sm" onClick={() => withdraw(r.id)}>Withdraw</button>
          )}
        </div>
      ))}
    </>
  );
}
